import { useState } from 'react';

/**
 * Holds back an error the field has carried since it mounted, until the reader has touched it.
 *
 * **A form must not open in a failed state.** Every create modal in this product rendered its
 * required fields red before the reader had typed anything — "Choose the date of examination
 * or conferment." on a form they had just opened. That is not a rejection, it is an
 * instruction, and colouring it like a rejection teaches people to ignore red exactly where it
 * matters. CLAUDE.md already said an error "only appears once the row is already wrong"; an
 * untouched empty field on a freshly opened form is not yet wrong.
 *
 * **The test is whether the error was there from the first render.** An error present at mount
 * is a prompt about an empty field; one that appears later is a reaction to something — the
 * reader typed a bad value, or the server rejected the save. So:
 *
 *  - present at mount, untouched → hidden
 *  - present at mount, touched → shown
 *  - appeared or changed after mount → shown, always
 *
 * That last line is what keeps a server rejection visible without any call site opting in:
 * `focusValidationError` lands on a field whose error arrived after mount, so it is already
 * revealed when focus gets there. It is also why a value that goes out of range while being
 * typed still turns red immediately — the message changed, so the reader caused it.
 *
 * The required asterisk and the disabled primary action are what say "not finished yet"
 * before any of this. Red is reserved for "you and I disagree about this value".
 */
export function useDeferredError(error?: string) {
  /*
    The error this input mounted with.

    A `useState` initializer, not a ref. The value is read during render, and a ref read during
    render is what React's rules forbid — a ref is not part of a render's own input, so the
    lint rule that flagged it was right. State seeded once and never set is: it is fixed at
    mount and never updates, which is exactly the question being asked.
  */
  const [initial] = useState(error);
  const [touched, setTouched] = useState(false);

  const isMountPrompt = error !== undefined && error === initial;

  return {
    /** What to render. Undefined while a mount-time prompt is still untouched. */
    error: isMountPrompt && !touched ? undefined : error,
    /** Wire to `onBlur`, and to `onChange` for controls with no meaningful blur. */
    markTouched: () => setTouched(true),
  };
}
